import type {CurriculumItemCtaCardProps, JBIVideoCardProps} from '@udemy/react-my-learning-unit';

import {NextCurriculumItem, UserEnrollmentsData} from 'src/types/page-props';

type CourseEnrollment = NonNullable<
    NonNullable<
        NonNullable<UserEnrollmentsData['me']['enrollments']>['courseEnrollmentsPaged']
    >['courseEnrollments'][number]
>;

export type CourseEnrollmentWithNextItem = CourseEnrollment & {
    course: CourseEnrollment['course'] & {
        nextCurriculumItem: NextCurriculumItem;
    };
};

type CourseContext = Pick<CourseEnrollment['course'], 'urlCourseTaking' | 'images'>;

type CourseContextForCtaCard = Pick<
    CourseEnrollment['course'],
    'title' | 'headline' | 'urlCourseTaking' | 'images'
>;

type VideoNextItem = Extract<
    NextCurriculumItem,
    {__typename: 'VideoLecture'} | {__typename: 'VideoMashupLecture'}
>;

type GraphQLMediaSource = NonNullable<NonNullable<VideoNextItem['asset']>['mediaSources']>[number];

const MEDIA_TYPE_TO_MIME: Record<string, string> = {
    APPLICATION_X_MPEGURL: 'application/x-mpegURL',
    APPLICATION_DASH_XML: 'application/dash+xml',
    VIDEO_MP4: 'video/mp4',
    AUDIO_MP3: 'audio/mpeg',
};

/** Returns true when the next curriculum item is a video lecture. */
export function isNextItemVideoLecture(nextItem: NextCurriculumItem): nextItem is VideoNextItem {
    return nextItem.__typename === 'VideoLecture' || nextItem.__typename === 'VideoMashupLecture';
}

/** Narrows a filtered course enrollment to one with a defined next curriculum item. */
export function asEnrollmentWithNextItem(
    enrollment: CourseEnrollment | null | undefined,
): CourseEnrollmentWithNextItem | null {
    const nextItem = enrollment?.course?.nextCurriculumItem;

    if (!enrollment || !nextItem) {
        return null;
    }

    return {
        ...enrollment,
        course: {
            ...enrollment.course,
            nextCurriculumItem: nextItem,
        },
    };
}

function getNextItemUrl(nextItem: NextCurriculumItem, courseUrl?: string | null): string {
    if ('urlLanding' in nextItem && nextItem.urlLanding) {
        return nextItem.urlLanding;
    }

    return courseUrl ?? '/';
}

function getNextItemDuration(nextItem: NextCurriculumItem): number {
    return 'durationInSeconds' in nextItem ? nextItem.durationInSeconds ?? 0 : 0;
}

function getNextItemThumbnail(
    nextItem: NextCurriculumItem,
    courseImage?: string | null,
): string | undefined {
    const itemThumbnail = 'thumbnail' in nextItem ? nextItem.thumbnail : undefined;

    return itemThumbnail || courseImage || undefined;
}

function getLastWatchedSecond(nextItem: NextCurriculumItem): number {
    return 'lastWatchedSecond' in nextItem ? nextItem.lastWatchedSecond ?? 0 : 0;
}

function mapMediaSources(
    mediaSources?: GraphQLMediaSource[] | null,
): JBIVideoCardProps['mediaSources'] {
    if (!mediaSources?.length) {
        return [];
    }

    return mediaSources.map(({src, mediaType, label}) => ({
        src,
        type: MEDIA_TYPE_TO_MIME[mediaType] ?? mediaType,
        label,
    }));
}

/** Maps GraphQL enrollment data to CurriculumItemCtaCard props. */
export function toCurriculumItemCtaCardProps(
    course: CourseContextForCtaCard,
    nextItem: NextCurriculumItem,
): CurriculumItemCtaCardProps {
    const durationInSeconds = getNextItemDuration(nextItem);
    const thumbnailUrl = getNextItemThumbnail(nextItem, course.images?.px240x135);

    return {
        typename: nextItem.__typename,
        itemTitle: nextItem.title,
        itemUrl: getNextItemUrl(nextItem, course.urlCourseTaking),
        courseTitle: course.title,
        ...(durationInSeconds > 0 ? {durationInSeconds} : {}),
        ...(course.headline ? {description: course.headline} : {}),
        ...(thumbnailUrl ? {thumbnailUrl} : {}),
    };
}

/** Maps GraphQL enrollment data to JBIVideoCard props. */
export function toJBIVideoCardProps(
    course: CourseContext,
    nextItem: VideoNextItem,
): JBIVideoCardProps {
    const asset = nextItem.asset ?? undefined;

    return {
        lectureTitle: nextItem.title,
        lectureUrl: getNextItemUrl(nextItem, course.urlCourseTaking),
        startTime: getLastWatchedSecond(nextItem),
        duration: getNextItemDuration(nextItem),
        assetId: Number.parseInt(asset?.id ?? nextItem.id, 10),
        thumbnailUrl: getNextItemThumbnail(nextItem, course.images?.px240x135),
        mediaSources: mapMediaSources(asset?.mediaSources),
        mediaLicenseToken: asset?.accessToken ?? undefined,
    };
}
